import React, { useCallback, useState } from 'react'
import { InputGroup, DropdownButton, Dropdown } from 'react-bootstrap'
import { getOptionsConfig } from '@rawgraphs/rawgraphs-core'
import { onChartExported } from '../../gaEvents'
import { buildExportSvg, getCanvasSize } from '../../services/exportSvg'
import {
  buildCanvasForExport,
  canDirectCanvasExport,
} from '../../services/exportCanvas'

function getDefaultExportFormat() {
  return 'jpg'
}

function downloadBlob(url, filename) {
  const a = document.createElement('a')
  a.href = url
  a.download = filename || 'download'
  a.click()
  return a
}

export default function Exporter({
  rawViz,
  exportProject,
  exportLugsailProject,
  brandTheme,
  currentChart,
  dataset,
  mapping,
  dataTypes,
  visualOptions,
  chartColorSyncMode = 'brand',
  hasMappedColorField = false,
}) {
  const [currentFormat, setCurrentFormat] = useState(getDefaultExportFormat)
  const [currentFile, setCurrentFile] = useState('viz')
  const [imageScale, setImageScale] = useState(1)
  const [downloadDone, setDownloadDone] = useState(false)

  const isRaster = currentFormat === 'png' || currentFormat === 'jpg'
  const supportsDirectCanvasExport = canDirectCanvasExport(currentChart)
  const recolorMarks = !getOptionsConfig(currentChart?.visualOptions)?.colorScale

  const flashDone = useCallback(() => {
    setDownloadDone(true)
    setTimeout(() => setDownloadDone(false), 2000)
  }, [])

  const downloadSvg = useCallback(
    (filename) => {
      return new Promise((resolve) => {
        const exportSvg = buildExportSvg(rawViz, brandTheme, {
          chartId: currentChart?.metadata?.id,
          mapping,
          dataTypes,
          visualOptions,
          recolorMarks,
        })
        if (!exportSvg) {
          resolve()
          return
        }

        const svgString = new XMLSerializer().serializeToString(exportSvg)
        const DOMURL = window.URL || window.webkitURL || window
        const svg = new Blob([svgString], {
          type: 'image/svg+xml;charset=utf-8',
        })
        const url = DOMURL.createObjectURL(svg)
        downloadBlob(url, filename)
        DOMURL.revokeObjectURL(url)
        resolve()
      })
    },
    [brandTheme, rawViz, currentChart, mapping, dataTypes, visualOptions, recolorMarks]
  )

  const downloadImage = useCallback(
    async (format, filename, scale) => {
      if (supportsDirectCanvasExport) {
        const canvas = await buildCanvasForExport({
          chart: currentChart,
          dataset,
          dataTypes,
          mapping,
          visualOptions,
          brandTheme,
          scale,
        })

        if (canvas) {
          downloadBlob(canvas.toDataURL(format), filename)
          return
        }
        // canvas was null (small dataset) — fall through to SVG path below
      }

      return new Promise((resolve, reject) => {
        const exportSvg = buildExportSvg(rawViz, brandTheme, {
          chartId: currentChart?.metadata?.id,
          mapping,
          dataTypes,
          visualOptions,
          recolorMarks,
        })
        if (!exportSvg) {
          reject(new Error('No chart available to export'))
          return
        }

        const svgString = new XMLSerializer().serializeToString(exportSvg)
        const DOMURL = window.URL || window.webkitURL || window
        const svg = new Blob([svgString], {
          type: 'image/svg+xml;charset=utf-8',
        })
        const url = DOMURL.createObjectURL(svg)
        const canvas = document.createElement('canvas')
        const { width, height } = getCanvasSize(exportSvg)
        canvas.width = width * scale
        canvas.height = height * scale
        const ctx = canvas.getContext('2d')
        ctx.scale(scale, scale)
        const img = new Image()
        img.onload = function () {
          ctx.drawImage(img, 0, 0, width, height)
          const dataUrl = canvas.toDataURL(format)
          downloadBlob(dataUrl, filename)
          DOMURL.revokeObjectURL(url)
          resolve()
        }
        img.onerror = function () {
          DOMURL.revokeObjectURL(url)
          reject(new Error('Unable to render export image'))
        }
        img.src = url
      })
    },
    [
      brandTheme,
      rawViz,
      mapping,
      dataTypes,
      visualOptions,
      supportsDirectCanvasExport,
      currentChart,
      dataset,
      recolorMarks,
    ]
  )

  const downloadProject = useCallback(
    async (filename) => {
      const project = await exportLugsailProject()
      if (!project) {
        return
      }
      const blob = new Blob([project], { type: 'application/json' })
      const DOMURL = window.URL || window.webkitURL || window
      const url = DOMURL.createObjectURL(blob)
      downloadBlob(url, filename)
      DOMURL.revokeObjectURL(url)
    },
    [exportLugsailProject]
  )

  const exportFormats = ['jpg', 'png', 'svg', 'lugsail']
  const previewSvg = rawViz
    ? buildExportSvg(rawViz, brandTheme, {
        chartId: currentChart?.metadata?.id,
        mapping,
        dataTypes,
        visualOptions,
        recolorMarks,
      })
    : null
  const previewCanvasSize = previewSvg
    ? getCanvasSize(previewSvg)
    : {
        width: visualOptions?.width || 1200,
        height: visualOptions?.height || 800,
      }

  const downloadViz = useCallback(async () => {
    switch (currentFormat) {
      case 'svg':
        await downloadSvg(`${currentFile}.svg`)
        break
      case 'png':
        await downloadImage('image/png', `${currentFile}.png`, imageScale)
        break
      case 'jpg':
        await downloadImage('image/jpeg', `${currentFile}.jpg`, imageScale)
        break
      case 'lugsail':
        await downloadProject(`${currentFile}.lugsail`)
        break
      default:
        break
    }
    if (rawViz?._chartImplementation?.metadata) {
      onChartExported(rawViz._chartImplementation.metadata, currentFormat)
    }
    flashDone()
  }, [
    currentFile,
    currentFormat,
    imageScale,
    downloadImage,
    downloadProject,
    downloadSvg,
    rawViz,
    flashDone,
  ])

  return (
    <div className="export-controls">
      <div className="export-field">
        <label className="small d-block mb-1">File name</label>
        <InputGroup className="raw-input-group export-input-group">
          <input
            type="text"
            className="form-control text-field"
            value={currentFile}
            onChange={(e) => setCurrentFile(e.target.value)}
          />
          <DropdownButton
            as={InputGroup.Append}
            title={`.${currentFormat}`}
            id="input-group-dropdown-1"
            className="raw-dropdown"
          >
            {exportFormats.map((format) => (
              <Dropdown.Item key={format} onClick={() => setCurrentFormat(format)}>
                .{format}
                {format === 'lugsail' && (
                  <span className="text-muted ml-1 small">— save editable project</span>
                )}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </InputGroup>
      </div>

      {isRaster && (
        <div className="export-field">
          <label className="small d-block mb-1">Raster scale</label>
          <DropdownButton
            title={`${imageScale}×`}
            variant="outline-secondary"
            className="raw-dropdown"
            id="scale-dropdown"
          >
            {[1, 2, 3].map((scale) => (
              <Dropdown.Item key={scale} onClick={() => setImageScale(scale)}>
                {scale}×
              </Dropdown.Item>
            ))}
          </DropdownButton>
          <div className="small text-muted mt-1">
            {Math.round(previewCanvasSize.width * imageScale)} ×{' '}
            {Math.round(previewCanvasSize.height * imageScale)} px
          </div>
        </div>
      )}

      <div className="export-actions">
        <button
          className="btn btn-primary raw-btn export-download-btn"
          onClick={downloadViz}
          disabled={!rawViz && !(isRaster && supportsDirectCanvasExport)}
        >
          {downloadDone ? 'Downloaded!' : 'Download'}
        </button>
      </div>
    </div>
  )
}
